import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { Notification, JobStatus, JobStatusResponse } from 'app/layout/common/notifications/notifications.types';
import { map, switchMap, take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class NotificationsService {
  private _notifications: BehaviorSubject<Notification[] | null>;
  private _refreshTrigger = new Subject<void>();

  readonly refreshTrigger$ = this._refreshTrigger.asObservable();

  /**
   * Constructor
   *
   * @param {HttpClient} _httpClient
   */
  constructor(private _httpClient: HttpClient) {
    // Set the private defaults
    this._notifications = new BehaviorSubject(null);
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Getter for notifications
   */
  get notifications$(): Observable<Notification[]> {
    return this._notifications.asObservable();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Store notifications on the service
   *
   * @param notifications
   */
  store(notifications: Notification[]): Observable<Notification[]> {
    // Load the notifications
    this._notifications.next(notifications);

    // Return the notifications
    return this.notifications$;
  }

  /**
   * Create a notification
   *
   * @param notification
   */
  create(notification: Notification): Observable<Notification> {
    return this.notifications$.pipe(
      take(1),
      switchMap((notifications) =>
        this._httpClient.put<Notification>('api/common/notifications', { notification }).pipe(
          map((newNotification) => {
            // Update the notifications with the new notification
            this._notifications.next([...notifications, newNotification]);

            // Return the new notification from observable
            return newNotification;
          }),
        ),
      ),
    );
  }

  /**
   * Update the notification
   *
   * @param id
   * @param notification
   */
  update(id: number, notification: Notification): Observable<Notification> {
    return this.notifications$.pipe(
      take(1),
      switchMap((notifications) =>
        this._httpClient
          .patch<Notification>('api/common/notifications', {
            id,
            notification,
          })
          .pipe(
            map((updatedNotification: Notification) => {
              // Find the index of the updated notification
              const index = notifications.findIndex((item) => item.id === id);

              // Update the notification
              notifications[index] = updatedNotification;

              // Update the notifications
              this._notifications.next(notifications);

              // Return the updated notification
              return updatedNotification;
            }),
          ),
      ),
    );
  }

  /**
   * Mark all notifications as read
   */
  markAllAsRead(): Observable<boolean> {
    return this.notifications$.pipe(
      take(1),
      switchMap((notifications) =>
        this._httpClient.get<boolean>('api/common/notifications/mark-all-as-read').pipe(
          map((isUpdated: boolean) => {
            // Go through all notifications and set them as read
            notifications.forEach((notification, index) => {
              notifications[index].is_read = true;
            });

            // Update the notifications
            this._notifications.next(notifications);

            // Return the updated status
            return isUpdated;
          }),
        ),
      ),
    );
  }

  getNotifications(): Observable<Notification[]> {
    return this._httpClient
      .get<{ system_notifications: Notification[] }>('user/notifications')
      .pipe(map((res) => res.system_notifications));
  }

  markNotificationsAsRead(): Observable<void> {
    return this._httpClient.post<void>(`user/read_notifications`, {});
  }

  getInProgressJobs(): Observable<JobStatus[]> {
    const params = new HttpParams()
      .append('status[]', 'in_progress')
      .append('job_purpose[]', 'object_import')
      .append('job_purpose[]', 'portfolio_creation');

    return this._httpClient.get<JobStatusResponse>('job_statuses', { params }).pipe(map((res) => res.job_statuses));
  }

  markNotificationAsRead(notificationId: number): Observable<void> {
    return this._httpClient.post<void>('user/read_notifications', {
      notification_ids: [notificationId],
    });
  }

  triggerRefresh(): void {
    this._refreshTrigger.next();
  }
}
